import React, { useState, useEffect } from 'react';
import { blogCommentAPI } from '../utils/api';
import { useAuth } from '../hooks/useAuthHook';
import {
  List,
  Avatar,
  Button,
  Input,
  message,
  Tooltip,
  Popconfirm,
  Empty,
  Spin,
  Divider
} from 'antd';
import {
  MessageOutlined,
  LikeOutlined,
  DeleteOutlined,
  ClockCircleOutlined
} from '@ant-design/icons';

const { TextArea } = Input;

const CommentList = ({ blogId, username }) => {
  const { user } = useAuth();
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [replyTo, setReplyTo] = useState(null);
  const [value, setValue] = useState('');

  useEffect(() => {
    fetchComments();
  }, [blogId]);

  const fetchComments = async () => {
    try {
      setLoading(true);
      const response = await blogCommentAPI.getCommentsByBlog(blogId);
      // 构建评论树形结构
      const commentTree = buildCommentTree(response.data.list || response.data || []);
      setComments(commentTree);
    } catch (error) {
      console.error('获取评论失败:', error);
      message.error('获取评论失败，请刷新页面重试');
    } finally {
      setLoading(false);
    }
  };

  // 构建评论树
  const buildCommentTree = (flatComments) => {
    const commentMap = {};
    const roots = [];

    // 创建评论映射
    flatComments.forEach(comment => {
      commentMap[comment.id] = { ...comment, children: [] };
    });

    // 构建树形结构
    flatComments.forEach(comment => {
      if (comment.parentId && commentMap[comment.parentId]) {
        commentMap[comment.parentId].children.push(commentMap[comment.id]);
      } else {
        roots.push(commentMap[comment.id]);
      }
    });

    return roots;
  };

  const handleSubmit = async () => {
    if (!user) {
      message.warning('请先登录后发表评论');
      return;
    }

    if (!value.trim()) {
      message.warning('评论内容不能为空');
      return;
    }

    setSubmitting(true);
    try {
      const commentData = {
        blogId,
        content: value.trim()
      };

      // 如果是回复评论，添加parentId和replyTo
      if (replyTo?.id) {
        commentData.parentId = replyTo.id;
        commentData.replyTo = replyTo.username;
      }

      await blogCommentAPI.addComment(commentData);
      message.success('评论发表成功');
      setValue('');
      setReplyTo(null);
      fetchComments();
    } catch (error) {
      console.error('评论失败:', error);
      message.error('评论发表失败，请稍后重试');
    } finally {
      setSubmitting(false);
    }
  };

  const handleReply = (comment) => {
    setReplyTo(comment);
    setValue(`@${comment.username} `);
  };

  const handleDelete = async (commentId) => {
    try {
      await blogCommentAPI.deleteComment(commentId);
      message.success('评论删除成功');
      fetchComments();
    } catch (error) {
      console.error('删除评论失败:', error);
      message.error('评论删除失败，请稍后重试');
    }
  };

  const canDelete = (comment) => {
    return user && (user.username === comment.userId || user.username === username);
  };

  const renderComment = (comment, isNested = false) => {
    return (
      <div key={comment.id} style={{ marginBottom: isNested ? 12 : 24 }}>
        <div style={{ display: 'flex', gap: 12 }}>
          <Avatar
            src={comment.avatar}
            icon={<MessageOutlined />}
            style={{ flexShrink: 0 }}
          >
            {comment.username?.charAt(0)?.toUpperCase()}
          </Avatar>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: 4 }}>
              <span style={{ fontWeight: 500, marginRight: 8 }}>
                {comment.username}
              </span>
              <Tooltip title={new Date(comment.createTime).toLocaleString('zh-CN')}>
                <span style={{ fontSize: '12px', color: '#999' }}>
                  <ClockCircleOutlined /> {formatDate(comment.createTime)}
                </span>
              </Tooltip>
            </div>
            <div style={{ marginBottom: 8 }}>
              {comment.replyTo && (
                <span style={{ color: '#1890ff', marginRight: 4 }}>
                  @{comment.replyTo}{' '}
                </span>
              )}
              <span style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                {comment.content}
              </span>
            </div>
            <div style={{ display: 'flex', gap: 16, fontSize: '13px' }}>
              <Button
                type="link"
                size="small"
                icon={<MessageOutlined />}
                onClick={() => handleReply(comment)}
                style={{ padding: 0, height: 'auto' }}
              >
                回复
              </Button>
              {canDelete(comment) && (
                <Popconfirm
                  title="确认删除"
                  description="确定要删除这条评论吗？"
                  onConfirm={() => handleDelete(comment.id)}
                  okText="确定"
                  cancelText="取消"
                >
                  <Button
                    type="link"
                    danger
                    size="small"
                    icon={<DeleteOutlined />}
                    style={{ padding: 0, height: 'auto' }}
                  >
                    删除
                  </Button>
                </Popconfirm>
              )}
            </div>
            {comment.children && comment.children.length > 0 && (
              <div style={{ marginTop: 16 }}>
                {comment.children.map(child => renderComment(child, true))}
              </div>
            )}
          </div>
        </div>
        {comment.children && comment.children.length > 0 && !isNested && (
          <Divider style={{ margin: '16px 0' }} />
        )}
      </div>
    );
  };

  const formatDate = (dateString) => {
    const now = new Date();
    const date = new Date(dateString);
    const diff = now - date;

    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return '刚刚';
    if (minutes < 60) return `${minutes}分钟前`;
    if (hours < 24) return `${hours}小时前`;
    if (days < 7) return `${days}天前`;

    return date.toLocaleDateString('zh-CN');
  };

  return (
    <div style={{ marginTop: 32 }}>
      <h3>评论 ({comments.length})</h3>

      {/* 评论输入框 */}
      {user && (
        <div style={{ marginBottom: 24, padding: 16, background: '#f5f5f5', borderRadius: 8 }}>
          <div style={{ display: 'flex', gap: 12 }}>
            <Avatar
              src={user.avatar}
              icon={<MessageOutlined />}
            >
              {user.username?.charAt(0)?.toUpperCase()}
            </Avatar>
            <div style={{ flex: 1 }}>
              <TextArea
                rows={4}
                value={value}
                onChange={(e) => setValue(e.target.value)}
                placeholder={
                  replyTo
                    ? `回复 @${replyTo.username}...`
                    : '写下你的评论...'
                }
                style={{ marginBottom: 8 }}
              />
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                {replyTo && (
                  <span style={{ color: '#1890ff', fontSize: '14px' }}>
                    回复 @{replyTo.username}
                    <Button
                      type="link"
                      size="small"
                      onClick={() => {
                        setReplyTo(null);
                        setValue('');
                      }}
                    >
                      取消
                    </Button>
                  </span>
                )}
                <Button
                  type="primary"
                  loading={submitting}
                  onClick={handleSubmit}
                  disabled={!value.trim()}
                >
                  发布评论
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {!user && (
        <div style={{ marginBottom: 24, textAlign: 'center', padding: '20px', background: '#f5f5f5', borderRadius: 4 }}>
          <Button type="link" onClick={() => window.location.href = '/login'}>
            登录后参与评论
          </Button>
        </div>
      )}

      {/* 评论列表 */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: 40 }}>
          <Spin />
        </div>
      ) : comments.length > 0 ? (
        <List
          dataSource={comments}
          renderItem={comment => renderComment(comment)}
          itemLayout="horizontal"
        />
      ) : (
        <Empty
          description="暂无评论，快来抢沙发吧！"
          style={{ padding: 40 }}
        />
      )}
    </div>
  );
};

export default CommentList;
